import ValidatedForm from 'core/components/validatedForm/ValidatedForm'
import React, { ReactNode, useMemo, useRef, useState, useEffect, useCallback } from 'react'
import Modal, { ModalProps } from './Modal'
import Button from '../button'
import useReactRouter from 'use-react-router'
import { Route } from 'core/plugins/route'
import Progress from 'core/components/progress/Progress'
import Alert from 'core/components/Alert'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import { ensureFunction } from 'utils/fp'
import clsx from 'clsx'
import Tooltip, { TooltipProps } from 'core/elements/tooltip/Tooltip'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import ConfirmationDialog from 'core/components/ConfirmationDialog'
import { trackEvent } from 'utils/tracking'

const CIRCLE_DIAMETER = 32

const useStyles = makeStyles<Theme>((theme) => ({
  stepModal: {
    display: 'grid',
    gap: 36,
    gridTemplateColumns: 'max-content max-content auto',
  },
  stepsContainer: {
    width: CIRCLE_DIAMETER,
  },
  steps: {
    display: 'flex',
    flexDirection: 'column',
    gap: CIRCLE_DIAMETER,
    position: 'fixed',
  },
  circle: {
    background: theme.palette.grey[200],
    borderRadius: '50%',
    height: CIRCLE_DIAMETER,
    width: CIRCLE_DIAMETER,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.grey[500],
    cursor: 'default',
    position: 'relative',
    '&.notFirst::before': {
      content: `''`,
      width: 1,
      height: CIRCLE_DIAMETER,
      background: theme.palette.grey[200],
      position: 'absolute',
      top: -CIRCLE_DIAMETER,
    },
  },
  active: {
    background: theme.palette.blue[700],
    color: theme.palette.grey['000'],
    '&.notFirst::before': {
      background: theme.palette.blue[700],
    },
    cursor: 'pointer',
  },
  separator: {
    width: 1,
    background: theme.palette.grey[200],
    margin: theme.spacing(-4, 0),
    minHeight: '100vh',
  },
  check: {
    color: theme.palette.grey['000'],
    fontSize: 14,
  },
}))

type PropsWithChildren<P = unknown> = P & {
  children?: ReactNode | ((any) => ReactNode) | undefined
}

interface BaseModalFormProps extends Omit<ModalProps, 'open'> {
  submitTitle?: string
  onSubmit?: any
  disableSubmit?: boolean
  fieldSetter?: any
  error?: {
    title?: string
    message?: string
  }
  customErrorComponent?: React.ReactNode
  loading?: boolean
  loadingMessage?: string
  submitting?: boolean
  initialValues?: Record<string, unknown>
  withAddonManager?: boolean
  clearOnSubmit?: boolean
  closeText?: string
  steps?: number
  disableSubmitMessage?: string
  hideBack?: boolean
  hideSubmit?: boolean
  onBackdropClick?: (event: React.MouseEvent) => void
  tooltipProps?: TooltipProps
  showConfirmationOnCancel?: boolean
}

interface PropsWithOpenRoute extends BaseModalFormProps {
  open?: undefined
  route: Route
}
interface PropsWithOpenFlag extends BaseModalFormProps {
  open: boolean
  route?: undefined
}

// Either route or open is required but not both
type ModalFormProps = PropsWithOpenRoute | PropsWithOpenFlag

export default function ModalForm({
  children,
  onSubmit,
  disableSubmit = false,
  submitTitle = 'Submit',
  closeText = 'Cancel',
  fieldSetter = null,
  submitting = false,
  loading = false,
  loadingMessage = 'Loading',
  error,
  customErrorComponent,
  route,
  open,
  withAddonManager,
  initialValues = {},
  steps,
  disableSubmitMessage,
  hideBack = false,
  hideSubmit = false,
  onBackdropClick,
  tooltipProps,
  showConfirmationOnCancel = false,
  title,
  ...props
}: PropsWithChildren<ModalFormProps>) {
  const classes = useStyles()
  const submitFuncRef = useRef(null)
  const { match } = useReactRouter()
  const [currentStep, setCurrentStep] = useState(1)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [hasSubmittedLastStep, setHasSubmittedLastStep] = useState(false)

  const renderStepsContent = ensureFunction(children)

  const setSubmitFuncRef = (handleSubmit) => {
    submitFuncRef.current = { handleSubmit }
  }

  const handleSubmit = useCallback(() => {
    // If on the last step, set hasSubmittedLastStep to true
    if (steps && currentStep === steps) {
      setHasSubmittedLastStep(true)
    }
    trackEvent(`${title} - Submitted`)
    submitFuncRef.current.handleSubmit()
  }, [title, currentStep, steps])

  const goNext = useCallback(
    (currentStep) => {
      setCurrentStep(currentStep + 1)
      trackEvent(`${title} - Go Next - Step ${currentStep}`)
      const modalBody = document.querySelector('.modal-body')
      modalBody.scrollTo(0, 0)
    },
    [title],
  )

  const goBack = useCallback(
    (currentStep) => {
      setCurrentStep(currentStep - 1)
      trackEvent(`${title} - Go Back - Step ${currentStep}`)
      const modalBody = document.querySelector('.modal-body')
      modalBody.scrollTo(0, 0)
    },
    [title],
  )

  const onValidationFail = useCallback(
    ({ step, results }) => {
      const failedFields = results.filter((prop) => prop.result === false).map((prop) => prop.key)
      if (step) {
        trackEvent(`${title} - Validation Failed - Step ${step}`, { failedFields })
      } else {
        trackEvent(`${title} - Validation Failed`, { failedFields })
      }
    },
    [title],
  )

  const toOpen = useMemo(() => (route ? route.pattern.match(match.url) : open), [match.url, open])

  useEffect(() => {
    setCurrentStep(1)
    setHasSubmittedLastStep(false)
  }, [toOpen])

  const handleClickOutside = onBackdropClick ? onBackdropClick : () => setShowConfirmation(true)

  const isFinalStepSubmitted = steps && currentStep === steps && hasSubmittedLastStep

  useEffect(() => {
    if (error && isFinalStepSubmitted) {
      setHasSubmittedLastStep(false)
    }
  }, [error, isFinalStepSubmitted])

  return (
    <Modal
      open={toOpen}
      footer={
        <>
          {!!error && <Alert variant="error" title={error?.title} message={error?.message} />}
          {customErrorComponent && customErrorComponent}
          {!isFinalStepSubmitted && (
            <Button
              variant="secondary"
              type="button"
              onClick={() =>
                showConfirmationOnCancel ? setShowConfirmation(true) : props.onClose()
              }
              disabled={submitting}
            >
              {closeText}
            </Button>
          )}
          {steps && currentStep > 1 && !hideBack && (
            <Button
              variant="secondary"
              type="button"
              onClick={() => goBack(currentStep)}
              disabled={submitting}
            >
              Back
            </Button>
          )}
          {onSubmit && !hideSubmit && (
            <Tooltip message={disableSubmit ? disableSubmitMessage : null} {...tooltipProps}>
              <Button onClick={handleSubmit} loading={submitting} disabled={disableSubmit}>
                {steps && currentStep < steps ? 'Next' : submitTitle}
              </Button>
            </Tooltip>
          )}
        </>
      }
      title={title}
      {...props}
      onBackdropClick={handleClickOutside}
    >
      {/* Was asked to only have the error in the footer, commenting out */}
      {/* {!!error && <Alert variant="error" title={error?.title} message={error?.message} />}
      {customErrorComponent && customErrorComponent} */}
      <Progress loading={loading} message={loadingMessage} renderContentOnMount={!!toOpen}>
        {/* On EditGroupPage, even though still loading, initialValues were populated
          Is adding this conditional better, or would reading updates to initialValues
          be better? */}
        {!loading && (
          <ValidatedForm
            onSubmit={onSubmit}
            triggerSubmit={setSubmitFuncRef}
            elevated={false}
            fieldSetter={fieldSetter}
            initialValues={initialValues}
            withAddonManager={withAddonManager}
            clearOnSubmit={props.clearOnSubmit}
            step={currentStep}
            steps={steps}
            onNextSuccess={goNext}
            onValidationFail={onValidationFail}
          >
            {steps ? (
              <div className={classes.stepModal}>
                <div className={classes.stepsContainer}>
                  <div className={classes.steps}>
                    {[...Array(steps).keys()].map((idx) => (
                      <div
                        key={idx}
                        className={clsx(
                          classes.circle,
                          currentStep > idx && classes.active,
                          idx > 0 && 'notFirst',
                        )}
                        onClick={() => {
                          if (isFinalStepSubmitted) return
                          if (currentStep > idx + 1) {
                            setCurrentStep(idx + 1)
                          }
                        }}
                      >
                        {currentStep > idx + 1 ? (
                          <FontAwesomeIcon className={classes.check}>check</FontAwesomeIcon>
                        ) : (
                          idx + 1
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div className={classes.separator} />
                {renderStepsContent({ step: currentStep })}
              </div>
            ) : (
              children
            )}
          </ValidatedForm>
        )}
      </Progress>
      {showConfirmation && (
        <ConfirmationDialog
          open={showConfirmation}
          onConfirm={() => {
            setShowConfirmation(false)
            props.onClose()
          }}
          title="Discard changes?"
          text="Are you sure you want to leave?"
          onCancel={() => setShowConfirmation(false)}
        />
      )}
    </Modal>
  )
}
